import { Text, type UseModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, useTheme } from "@mui/material";
import { logEvent } from "@src/appV2/lib/analytics";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics/events";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { type Worker } from "@src/appV2/Worker/api/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useFavoriteWorkplaces } from "@src/appV2/Workplaces/api/useFavoriteWorkplaces";
import { parseISO } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { formatInTimeZone } from "date-fns-tz";
import pluralize from "pluralize";
import { useMemo } from "react";

import { GroupedList } from "../../components/GroupedList";
import { ListLoadingState } from "../../components/ListLoadingState";
import { PickerButton } from "../../components/PickerButton";
import { OpenShiftCardContainer } from "../../Shift/Open/CardContainer";
import { ShiftListDateHeader } from "../../Shift/Open/ListDateHeader";
import { type OpenShiftListItem } from "../../Shift/Open/types";
import { convertShiftToSlotId } from "../../Shift/Open/useGetShiftsSlotV2";
import { useOpenShiftListDataContext } from "../../Shift/Open/useOpenShiftListDataContext";
import { useVirtualShiftListContext } from "../../Shift/Open/useVirtualShiftListContext/useVirtualShiftListContext";
import { formatDayOfMonthWithDayName } from "../../utils/formatDayOfMonthWithDayName";
import { NoFavoriteWorkplacesEmptyState } from "./NoFavoriteWorkplacesEmptyState";
import { NoShiftsEmptyState } from "./NoShiftsEmptyState";

interface FavoriteWorkplacesShiftsListProps {
  scrollRef: React.RefObject<HTMLDivElement>;
  pickerModalState: UseModalState;
}

function derivePageStateForAnalytics(params: {
  useFavoriteWorkplacesResult: ReturnType<typeof useFavoriteWorkplaces>;
  useOpenShiftListDataContextResult: ReturnType<typeof useOpenShiftListDataContext>;
  areShiftsShown: boolean;
}) {
  const { useFavoriteWorkplacesResult, useOpenShiftListDataContextResult, areShiftsShown } = params;
  if (
    useFavoriteWorkplacesResult.status === "loading" ||
    useOpenShiftListDataContextResult.isOpenShiftsLoading === true
  ) {
    return undefined;
  }

  if (useFavoriteWorkplacesResult.isError || useOpenShiftListDataContextResult.isOpenShiftsError) {
    return "error_loading_shifts_or_favorite_workplaces";
  }

  if (useFavoriteWorkplacesResult.favoriteWorkplacesCount > 0) {
    return areShiftsShown
      ? "shifts_shown_for_favorite_workplaces"
      : "no_shifts_shown_for_favorite_workplaces";
  }

  return "no_favorite_workplaces_selected";
}

function groupBy(shift: OpenShiftListItem, worker: Worker) {
  return formatInTimeZone(
    new Date(shift.type === "open-shift" ? shift.attributes.start : (shift.start ?? "")),
    worker.tmz,
    "yyyy-MM-dd"
  );
}

export function FavoriteWorkplacesShiftsList(props: FavoriteWorkplacesShiftsListProps) {
  const { scrollRef, pickerModalState } = props;
  const theme = useTheme();
  const worker = useDefinedWorker();
  const spacingX = theme.spacing(5);

  const { virtualShiftListItems: items } = useVirtualShiftListContext();
  // Filter out group headers and only keep actual shifts
  const virtualShiftListItems = useMemo(
    () =>
      items.filter(
        (item): item is OpenShiftListItem => "type" in item && item.type === "open-shift"
      ),
    [items]
  );

  const useFavoriteWorkplacesResult = useFavoriteWorkplaces();
  const {
    favoriteWorkplaces,
    favoriteWorkplacesCount,
    isLoading: isFavoriteWorkplacesLoading,
  } = useFavoriteWorkplacesResult;

  const isListEmpty = virtualShiftListItems.length === 0;

  const useOpenShiftListDataContextResult = useOpenShiftListDataContext();
  const {
    onClickOpenShift,
    workplacesMap,
    shiftsBlockingRequirementsContextMap,
    isOpenShiftsLoading,
    isOpenShiftsError,
    isOpenShiftsSuccess,
    shiftsSlotsMap,
    friendsMap,
    recentColleaguesMap,
    canLoadMore,
    loadMore,
    handleRangeChanged,
    shiftAuctionsMap,
    isLoadingMore,
  } = useOpenShiftListDataContextResult;

  const pageState = derivePageStateForAnalytics({
    useFavoriteWorkplacesResult,
    useOpenShiftListDataContextResult,
    areShiftsShown: virtualShiftListItems.length > 0,
  });
  useLogEffect(
    APP_V2_USER_EVENTS.FAVORITE_WORKPLACES_SHIFT_PAGE_VIEWED,
    {
      state: pageState,
    },
    {
      enabled: isDefined(pageState),
    }
  );

  if (isOpenShiftsError) {
    return (
      <Text sx={{ mx: spacingX }} align="center">
        Error loading favorite workplace shifts
      </Text>
    );
  }

  if (isFavoriteWorkplacesLoading) {
    return (
      <ListLoadingState
        wrapperSx={{ px: spacingX }}
        texts={[
          "Loading your favorite workplaces",
          "Fetching your workplace preferences",
          "Retrieving your saved facilities",
        ]}
      />
    );
  }

  if (isOpenShiftsLoading) {
    return (
      <ListLoadingState
        wrapperSx={{ px: spacingX }}
        texts={[
          "Loading shifts from your favorite workplaces",
          "Finding opportunities at your preferred facilities",
          "Searching for shifts at your favorite locations",
          "Analyzing your workplace preferences",
          "Discovering your next great opportunity",
        ]}
      />
    );
  }

  // Show appropriate empty state based on whether user has favorite workplaces
  if (isOpenShiftsSuccess && isListEmpty && favoriteWorkplacesCount === 0) {
    return <NoFavoriteWorkplacesEmptyState pickerModalState={pickerModalState} />;
  }

  if (isOpenShiftsSuccess && isListEmpty) {
    return (
      <NoShiftsEmptyState
        favoriteWorkplaces={favoriteWorkplaces}
        pickerModalState={pickerModalState}
      />
    );
  }

  return (
    <GroupedList
      customScrollParentRef={scrollRef}
      isLoadingMore={isLoadingMore}
      backgroundColor={theme.palette.background.tertiary}
      items={virtualShiftListItems}
      groupBy={(item) => groupBy(item, worker)}
      renderListHeader={() => (
        <Box sx={{ width: "100%", px: spacingX, py: 4 }}>
          <PickerButton
            fullWidth
            size="medium"
            aria-label="Manage Favorite Workplaces"
            primaryText={`${favoriteWorkplacesCount} favorite ${pluralize(
              "workplace",
              favoriteWorkplacesCount
            )} selected`}
            secondaryText={
              <Text variant="body2" color="primary" textAlign="right" flexGrow={1} pr={2}>
                Edit
              </Text>
            }
            startIconType="building"
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.UPDATE_FAVORITE_WORKPLACES_BUTTON_CLICKED, {
                state: "shifts_shown",
              });
              pickerModalState.openModal();
            }}
          />
        </Box>
      )}
      renderGroupHeader={(dateIso, count) => (
        <ShiftListDateHeader
          key={dateIso}
          label={formatDayOfMonthWithDayName(parseISO(dateIso))}
          totalShiftsCount={count}
          sx={{ px: spacingX }}
        />
      )}
      renderItem={(item) => {
        if (item.type !== "open-shift") {
          // Skip non-open-shift items (like in-app ads)
          return null;
        }

        const shift = item;
        const workplace = workplacesMap.get(shift.relationships.workplace.data.id);
        if (!isDefined(workplace)) {
          return null;
        }

        const shiftAuctionId = shift.relationships.shiftAuction?.data.id;
        const shiftAuction = shiftAuctionId ? shiftAuctionsMap.get(shiftAuctionId) : undefined;

        return (
          <OpenShiftCardContainer
            key={shift.id}
            isFavoriteWorkplace
            workers={
              shiftsSlotsMap
                .get(
                  convertShiftToSlotId({
                    start: shift.attributes.start,
                    end: shift.attributes.end,
                    facilityId: workplace.id,
                  })
                )
                ?.shiftAssignments.filter((assignment) => assignment.shiftId !== shift.id)
                .map(({ worker }) => worker) ?? []
            }
            shift={shift}
            workplace={workplace}
            blockingRequirementsContext={(() => {
              const context = shiftsBlockingRequirementsContextMap.get(shift.id);
              return context === null ? undefined : context;
            })()}
            friendsMap={friendsMap}
            shiftAuction={shiftAuction}
            recentColleaguesMap={recentColleaguesMap}
            sx={{ mx: spacingX }}
            onClick={({ offer }) => {
              onClickOpenShift({ shift, workplace, offer, shiftAuction });
            }}
          />
        );
      }}
      endReached={() => {
        if (canLoadMore) {
          loadMore();
        }
      }}
      rangeChanged={handleRangeChanged}
      loadingText="Loading more shifts..."
    />
  );
}
